/**
 * Partner Autocomplete Component
 * 
 * A shared component for searching and selecting partners.
 * Uses MUI Autocomplete with React Query for data fetching.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Search, Store, MapPin, Phone } from 'lucide-react';
import { useSearchPartners, Partner } from '../../../hooks/queries';

// ============================================================================
// LANGUAGE DETECTION HELPERS
// ============================================================================

type SearchLanguage = 'he' | 'ar' | 'en' | 'id';

/**
 * Detect the language of the search query based on character ranges
 */
function detectSearchLanguage(query: string): SearchLanguage {
  if (/[\u0590-\u05FF]/.test(query)) return 'he';  // Hebrew characters
  if (/[\u0600-\u06FF]/.test(query)) return 'ar';  // Arabic characters
  if (/^\d+$/.test(query.trim())) return 'id';     // Numeric = ID search (default to Hebrew)
  return 'en';                                      // Default to English
}

/**
 * Get partner name based on detected search language
 * Prioritizes the language matching the search, with fallbacks
 */
function getPartnerNameByLanguage(partner: Partner, lang: SearchLanguage): string {
  switch (lang) {
    case 'ar':
      return partner.businessNameAr || partner.businessNameHe || partner.businessNameEn || `ID: ${partner.businessId}`;
    case 'he':
      return partner.businessNameHe || partner.businessNameEn || partner.businessNameAr || `ID: ${partner.businessId}`;
    case 'en':
    case 'id':
    default:
      return partner.businessNameEn || partner.businessNameHe || partner.businessNameAr || `ID: ${partner.businessId}`;
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface PartnerAutocompleteProps {
  /** Called when a partner is selected */
  onSelect: (partner: Partner | null) => void;
  /** Currently selected partner */
  value?: Partner | null;
  /** Placeholder text */
  placeholder?: string;
  /** Label for the input */
  label?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Custom class name */
  className?: string;
  /** Minimum characters before search */
  minSearchLength?: number;
  /** Maximum results to show */
  maxResults?: number;
  /** Accent color for styling */
  accentColor?: string;
  /** Custom input padding (e.g., '10px 12px') */
  inputPadding?: string;
  /** Custom input font size (e.g., '14px') */
  inputFontSize?: string;
  /** Language used when showing selected partner and dropdown labels (default: detect from input) */
  displayLanguage?: SearchLanguage;
  /**
   * Replace the global partner search. Used so Account Managers only see
   * partners assigned to them.
   */
  searchPartners?: (query: string, limit: number) => Promise<Partner[]>;
  /** Stable cache key when `searchPartners` is set */
  searchScopeKey?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const PartnerAutocomplete: React.FC<PartnerAutocompleteProps> = ({
  onSelect,
  value = null,
  placeholder = 'Search partner...',
  label = 'Partner',
  disabled = false,
  required = false,
  error,
  className,
  minSearchLength = 1,
  maxResults = 20,
  accentColor = '#c00035',
  inputPadding,
  inputFontSize,
  displayLanguage,
  searchPartners,
  searchScopeKey = 'custom',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Keep visible text in sync with selected value (important after refresh)
  useEffect(() => {
    if (!value) {
      setInputValue('');
      return;
    }
    setInputValue(getPartnerNameByLanguage(value, displayLanguage ?? 'en'));
  }, [displayLanguage, value]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Detect search language based on input characters
  const detectedLanguage = useMemo(() => detectSearchLanguage(inputValue), [inputValue]);
  const optionLanguage = displayLanguage ?? detectedLanguage;

  const useScopedSearch = Boolean(searchPartners);
  const canSearch = debouncedQuery.length >= minSearchLength;

  const { data: defaultOptions = [], isLoading: defaultLoading } = useSearchPartners(
    debouncedQuery,
    maxResults,
    !useScopedSearch && canSearch,
  );

  const { data: scopedOptions = [], isLoading: scopedLoading } = useQuery({
    queryKey: ['partners', 'scoped-search', searchScopeKey, debouncedQuery, maxResults],
    queryFn: () => searchPartners!(debouncedQuery, maxResults),
    enabled: useScopedSearch && canSearch,
    staleTime: 30 * 1000,
  });

  const options = useScopedSearch ? scopedOptions : defaultOptions;
  const isLoading = useScopedSearch ? scopedLoading : defaultLoading;
  const resolvedPadding = inputPadding || '10px 14px';
  const resolvedFontSize = inputFontSize || '1rem';

  // Handle input change
  const handleInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  // Handle selection
  const handleChange = useCallback((_event: React.SyntheticEvent, newValue: Partner | null) => {
    onSelect(newValue);
  }, [onSelect]);

  // Render option
  const renderOption = useCallback((
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Partner
  ) => {
    const displayName = getPartnerNameByLanguage(option, optionLanguage);
    const { key: _key, ...otherProps } =
      props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };

    return (
      <li key={option.businessId} {...otherProps}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, width: '100%' }}>
          {/* Logo */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '10px',
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {option.logoUrl ? (
              <img
                src={option.logoUrl}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Store size={24} color="#94a3b8" />
            )}
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#1e293b',
                fontSize: '14px',
                lineHeight: 1.3,
              }}
            >
              {displayName}
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: '#64748b', fontSize: '12px', display: 'block' }}
            >
              ID: {option.businessId}
              {option.businessType && ` • ${option.businessType}`}
            </Typography>

            {option.businessAddress && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', mt: 0.5 }}>
                <MapPin size={12} />
                <Typography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    fontSize: '12px',
                  }}
                >
                  {option.businessAddress}
                </Typography>
              </Box>
            )}

            {option.phone1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', mt: 0.25 }}>
                <Phone size={12} />
                <Typography variant="caption" sx={{ fontSize: '12px' }}>
                  {option.phone1}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </li>
    );
  }, [optionLanguage]);

  // Get option label
  const getOptionLabel = useCallback((option: Partner): string => {
    return getPartnerNameByLanguage(option, optionLanguage);
  }, [optionLanguage]);

  // Check if options are equal
  const isOptionEqualToValue = useCallback((option: Partner, val: Partner): boolean => {
    return option.businessId === val.businessId;
  }, []);

  // No options text
  const noOptionsText = useMemo(() => {
    if (isLoading) return 'Searching...';
    return inputValue.length < minSearchLength
      ? `Type at least ${minSearchLength} characters to search`
      : 'No partners found';
  }, [inputValue.length, minSearchLength, isLoading]);

  return (
    <Autocomplete
      className={className}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={isLoading}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      loadingText="Searching..."
      slotProps={{
        popper: {
          sx: { zIndex: 10000 },
        },
      }}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          minHeight: '42px',
          height: '42px',
          backgroundColor: '#f8fafc',
          transition: 'all 0.2s ease',
          '& .MuiOutlinedInput-input': {
            padding: resolvedPadding,
            fontSize: resolvedFontSize,
          },
          '&:hover': {
            backgroundColor: '#f1f5f9',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: `0 0 0 3px ${accentColor}25`,
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e2e8f0',
        },
        '& .MuiInputLabel-root': {
          color: '#64748b',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search
                  size={18}
                  style={{ marginLeft: 8, marginRight: 4, color: '#94a3b8' }}
                />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={20} sx={{ color: accentColor }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiFormLabel-asterisk': {
              color: accentColor,
            },
          }}
        />
      )}
    />
  );
};

// Re-export Partner type for convenience
export type { Partner };

export default PartnerAutocomplete;





